import Cookie from 'js-cookie'
import { captureException } from '@/utilities/logger-fe'
import { CART_STORAGE_KEY } from '@/constants/shopify'
import type { Cart, CartLineInput } from '@/types/shopify'
import {
  addToCartOperation,
  updateToCartOperation,
  removeFromCartOperation,
  getCartOperation,
  createCartOperation,
} from '@/stores/cart/operations'
import { getFlagVariantBySKU } from '@/clients/sanity/queries/product'

let cartId = Cookie.get(CART_STORAGE_KEY)

export const initCart = async () => {
  const reinit = async () => {
    try {
      const cart = await createCartOperation()

      if (!cart?.id || !cart?.lines) {
        captureException('Unable to create cart.')
      }

      cartId = cart.id
      Cookie.set(CART_STORAGE_KEY, cartId, { expires: 10 })

      return cart
    } catch {
      captureException('Unable to create cart.')
    }
  }

  try {
    if (cartId) {
      const cart = await getCartOperation(cartId)
      if (!cart?.lines) {
        return await reinit()
      }

      return cart
    } else {
      return await reinit()
    }
  } catch {
    captureException('Unable to create cart.')
  }
}

export const addItems = async (
  lines: {
    merchandiseId: string
    quantity: number
    sellingPlanId?: string
    attributes?: { key: string; value: string }[]
  }[],
) => {
  if (!cartId || !lines?.length) {
    return
  }

  try {
    const cart = await addToCartOperation(cartId, lines)

    if (!cart?.lines) {
      captureException('Unable to add items to cart.')
    }

    return cart
  } catch {
    captureException('Unable to add items to cart.')
  }
}

export const removeItems = async (lineIds: string[]) => {
  if (!cartId || !lineIds?.length) {
    return
  }

  try {
    const cart = await removeFromCartOperation(cartId, lineIds)

    if (!cart?.lines) {
      captureException('Unable to remove items from cart.')
    }

    return cart
  } catch {
    captureException('Unable to remove items from cart.')
  }
}

export const updateItems = async (
  lines: CartLineInput[],
): Promise<Cart | undefined> => {
  if (!cartId || !lines?.length) {
    return
  }

  try {
    const cart = await updateToCartOperation(cartId, lines)

    if (!cart?.lines) {
      captureException('Unable to retrieve line items from cart.')

      return
    }

    return cart
  } catch {
    captureException('Unable to remove items from cart.')
  }
}

export const getCartItemCount = (cart: Cart) => {
  return cart.lines.reduce((acc, line) => {
    return acc + line.quantity
  }, 0)
}

export const getItemsWithRequiredLogin = async (
  skuList: string[],
  idList: string[],
) => {
  const itemsFlagged = await getFlagVariantBySKU(skuList, idList)
  return itemsFlagged.some((item: any) => item.userLoggedInRequired)
}
